"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useTranslation } from "../../../../i18n/client";
import Button from "./Button";
import { LanguageSelector } from "./LanguageSelector";
import LoginIcon from "../../../../../public/images/New-landing/login-icon.svg";
import OmtoLogoBack from "../../../../../public/images/New-landing/omto-logo-back.svg";
import { Routes } from "../../../(utils)/routes";
import OmtoLogo from "../../../../../public/images/New-landing/omto-logo-complete.svg";

interface HeaderProps {
  lng: string;
}

const Header: React.FC<HeaderProps> = ({ lng }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const params = useParams();
  const { t }: { t: any } = useTranslation(lng, "new-landing");

  useEffect(() => {
    const handleScroll = () => {
      const aboutSection = document.getElementById("about");
      if (aboutSection) {
        const rect = aboutSection.getBoundingClientRect();
        setIsScrolled(rect.top <= 80);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigationItems = [
    { label: t("header-navigation.about-us"), href: "#about" },
    { label: t("header-navigation.products"), href: "#products" },
    { label: t("header-navigation.contact-us"), href: "#contact" },
  ];

  return (
    <header 
      className={`fixed top-0 z-[11] w-full h-20 transition-all duration-300 max-w-[100vw] ${
        isScrolled 
          ? "bg-[#031F47] shadow-[0_2px_18px_3px_rgba(0,0,0,0.5)]" 
          : "header-overlay shadow-[0_2px_18px_3px_rgba(0,0,0,0.5)]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="md:hidden z-[51]"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Open menu"
          >
            <div className="flex flex-col gap-1 group cursor-pointer w-max">
              <span
                className={`block h-[2.5px] bg-white transform skew-x-[-20deg] transition-all duration-300 ${menuOpen ? "w-6" : "w-6"}`}
              />
              <span
                className={`block h-[2.5px] bg-white transform skew-x-[-20deg] transition-all duration-300 ${menuOpen ? "w-6" : "w-5"}`}
              />
              <span
                className={`block h-[2.5px] bg-white transform skew-x-[-20deg] transition-all duration-300 ${menuOpen ? "w-6" : "w-4"}`}
              />
            </div>
          </button>

          <Link href={`/${Routes.Home}`}>
            <OmtoLogo width={102} height={27} />
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-16">
          <nav className="flex items-center gap-4 lg:gap-8">
            <LanguageSelector />

            {navigationItems.map((item) => (
              <a key={item.label} href={item.href} className="nav-link">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4 lg:gap-8">
            <Button
              variant="contact"
              size="md"
              className="hidden md:block font-extrabold"
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              {t("header-contact")}
            </Button>
            <Link
              className="whitespace-nowrap text-b1 font-medium text-primary hover:text-primary-200"
              href={`/${params?.lng || "en"}${Routes.SignIn}`}
            >
              <LoginIcon />
            </Link>
          </div>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <LanguageSelector />
          <Link
            className="p-2"
            href={`/${params?.lng || "en"}${Routes.SignIn}`}
          >
            <LoginIcon />
          </Link>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden w-screen h-dvh">
          <div className="absolute inset-y-0 left-0 shadow-2xl bg-black/90 w-screen h-dvh menu-slide-in">
            <div className="flex flex-col h-full">
              <nav className="flex-1 flex flex-col justify-center items-center p-6">
                <div className="mb-8">
                  <Link
                    href={`/${Routes.Home}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <OmtoLogoBack />
                  </Link>
                </div>
                <div className="space-y-2 text-center">
                  {navigationItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="nav-link-mobile"
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
